import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PoliciesHttpService } from '@app/http/policies-http.service';
import { UsersHttpService } from '@app/http/users-http.service';
import { IPendingPolicyDto, IPolicyContentDto } from '@app/interfaces/policy.interface';
import { AppLanguageService } from '@app/services/app-language.service';
import { TPolicyType } from '@app/types/policy.type';
import { translations } from '@app/utils/translations';
import {
    CUSTOM_DIALOG_CONFIG,
    CUSTOM_DIALOG_REF,
    IButtonAction,
    IKuiDialogConfig,
    INothingFound,
    KuiAlertComponent,
    KuiDialogComponent,
    KuiDialogRef,
    KuiNothingFoundComponent,
    KuiSpinnerComponent,
} from '@kone/ui';
import { TranslatePipe } from '@ngx-translate/core';

/**
 * `consent`: blocking pop-up for the policies the API reported as pending; closes with `true` once accepted.
 * `view`: read-only view of the currently published document of a type.
 */
export type TPolicyDialogData =
    { mode: 'consent'; policies: IPendingPolicyDto[] } | { mode: 'view'; type: TPolicyType };

@Component({
    selector: 'app-policy-dialog',
    template: `
        <kui-dialog [header]="header()" [actions]="actions()" [hasClose]="data.mode === 'view'">
            @if (loading()) {
                <kui-spinner [text]="translations.global.loading" />
            } @else if (!documents().length) {
                <kui-nothing-found [data]="nothingFound()" />
            } @else {
                @for (document of documents(); track document.type) {
                    @if (!$first) {
                        <hr class="my-6" />
                    }
                    @if (data.mode === 'consent') {
                        <h3 class="font-bold mb-1">
                            {{ translations.policies.types._base + document.type | translate }}
                        </h3>
                    }
                    <p class="text-soft text-xs mb-item">
                        {{ translations.global.version | translate }} {{ document.version }}
                    </p>
                    <div class="rich-text text-sm" [innerHTML]="document.content"></div>
                }
            }
            <kui-alert
                [message]="alertMsg()"
                color="warn"
                background="warn-background"
                icon="alert-triangle"
                class="mt-item" />
        </kui-dialog>
    `,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        KuiDialogComponent,
        KuiSpinnerComponent,
        KuiNothingFoundComponent,
        KuiAlertComponent,
        TranslatePipe,
    ],
})
export class PolicyDialogComponent {
    protected readonly translations = translations;

    private readonly ref = inject<KuiDialogRef>(CUSTOM_DIALOG_REF);
    private readonly usersHttpService = inject(UsersHttpService);
    private readonly policiesHttpService = inject(PoliciesHttpService);
    private readonly appLanguageService = inject(AppLanguageService);

    readonly data = inject<IKuiDialogConfig<TPolicyDialogData>>(CUSTOM_DIALOG_CONFIG).data!;

    readonly documents = signal<IPolicyContentDto[]>(
        this.data.mode === 'consent' ? this.data.policies : [],
    );
    readonly loading = signal(this.data.mode === 'view');
    readonly loadFailed = signal(false);
    readonly accepting = signal(false);
    readonly alertMsg = signal('');

    readonly header = computed(() =>
        this.data.mode === 'consent'
            ? translations.terms.dialog.title
            : translations.policies.types._base + this.data.type,
    );
    readonly nothingFound = computed<INothingFound>(() => ({
        message: this.loadFailed()
            ? translations.global.something_went_wrong
            : translations.policies.not_available,
    }));
    readonly actions = computed<IButtonAction<unknown>[]>(() => {
        if (this.data.mode === 'view') return [];
        return [
            {
                type: () => 'tertiary',
                color: () => 'primary',
                label: () => translations.terms.dialog.decline,
                disabled: () => this.accepting(),
                action: () => this.ref.close(false),
            },
            {
                type: () => 'primary',
                color: () => 'primary',
                label: () => translations.terms.dialog.accept,
                loading: () => this.accepting(),
                action: () => this.accept(),
            },
        ];
    });

    constructor() {
        if (this.data.mode === 'view') {
            this.policiesHttpService
                .getContent(this.data.type, this.appLanguageService.current())
                .pipe(takeUntilDestroyed())
                .subscribe({
                    next: content => {
                        this.documents.set(content ? [content] : []);
                        this.loading.set(false);
                    },
                    error: () => {
                        this.loadFailed.set(true);
                        this.loading.set(false);
                    },
                });
        }
    }

    accept() {
        if (this.data.mode !== 'consent' || this.accepting()) return;

        this.accepting.set(true);
        this.alertMsg.set('');

        const accepted = this.data.policies.map(({ scope, type, version }) => ({
            scope,
            type,
            version,
        }));
        this.usersHttpService.acceptPolicies(accepted).subscribe({
            next: () => this.ref.close(true),
            error: () => {
                this.accepting.set(false);
                this.alertMsg.set(translations.policies.accept_error);
            },
        });
    }
}
